"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClinicalErrorCatalog = exports.ClinicalErrorCodes = void 0;
exports.getClinicalErrorCatalogEntry = getClinicalErrorCatalogEntry;
/**
 * Clinical-specific error codes — namespaced `ENCOUNTER_*`/
 * `CLINICAL_DOCUMENT_*`/`CLINICAL_ORDER_*`/`CLINICAL_APPROVAL_*`,
 * additive and parallel to backend's own `ErrorCodes`/`ErrorCatalog`
 * (`backend/src/shared/constants/error-codes.constant.ts`,
 * `backend/src/core/exception/error-catalog.ts`), which this package
 * does not import or modify — same discipline
 * `patient-error-codes.ts`/`appointment-error-codes.ts`/
 * `financial-error-codes.ts` already established (3 real precedents).
 * Each code here maps to a real, named condition already thrown
 * inside a frozen Clinical Domain Service (Stage 6C.2) or Application
 * Service (Stage 6C.3) — this file does not invent new rules, only
 * gives ones that already exist a stable, documented code a client
 * can branch on.
 *
 * Collision check (verified against all 3 existing registries before
 * adding these, `CLINICAL-ERROR-GUIDE.md`): Patient uses
 * `PATIENT_*`/`MERGE_*`/`COVERAGE_*`/`CONSENT_*`/`PROTECTION_*`/
 * `IDENTITY_*`; Appointment uses `APPOINTMENT_*`/`CHECK_IN_*`/
 * `DOCTOR_*`/`QUEUE_*`/`REMINDER_*`/`SEQUENCE_*`/`SLOT_HOLD_*`/
 * `TELEMEDICINE_*`/`WAITLIST_*`; Financial uses `PAYMENT_*`/
 * `RECEIPT_*`/`ADVANCE_DEPOSIT_*`/`REFUND_*`/`TARIFF_*`/`PACKAGE_*`/
 * `DISCOUNT_*`/`BILLING_*`. None of the prefixes below overlap any of
 * them — re-verified by the collision-guard unit test in
 * `tests/unit/contracts/api/clinical-compatibility.test.ts`, which
 * fails loudly if a future edit introduces one.
 */
exports.ClinicalErrorCodes = {
    ENCOUNTER_INVALID_STATUS_TRANSITION: 'ENCOUNTER_INVALID_STATUS_TRANSITION',
    ENCOUNTER_DOCUMENTATION_REQUIRED: 'ENCOUNTER_DOCUMENTATION_REQUIRED',
    ENCOUNTER_ALREADY_FINALIZED: 'ENCOUNTER_ALREADY_FINALIZED',
    ENCOUNTER_OPTIMISTIC_LOCK_CONFLICT: 'ENCOUNTER_OPTIMISTIC_LOCK_CONFLICT',
    CLINICAL_DOCUMENT_TEMPLATE_INVALID: 'CLINICAL_DOCUMENT_TEMPLATE_INVALID',
    CLINICAL_DOCUMENT_NOT_DRAFT: 'CLINICAL_DOCUMENT_NOT_DRAFT',
    CLINICAL_DOCUMENT_INVALID_STATUS_TRANSITION: 'CLINICAL_DOCUMENT_INVALID_STATUS_TRANSITION',
    CLINICAL_DOCUMENT_NOT_FINAL: 'CLINICAL_DOCUMENT_NOT_FINAL',
    CLINICAL_DOCUMENT_OPTIMISTIC_LOCK_CONFLICT: 'CLINICAL_DOCUMENT_OPTIMISTIC_LOCK_CONFLICT',
    CLINICAL_ORDER_INVALID_REFERENCE: 'CLINICAL_ORDER_INVALID_REFERENCE',
    CLINICAL_ORDER_DUPLICATE_ACTIVE: 'CLINICAL_ORDER_DUPLICATE_ACTIVE',
    CLINICAL_ORDER_INVALID_STATUS_TRANSITION: 'CLINICAL_ORDER_INVALID_STATUS_TRANSITION',
    CLINICAL_ORDER_ALREADY_CANCELLED: 'CLINICAL_ORDER_ALREADY_CANCELLED',
    CLINICAL_ORDER_OPTIMISTIC_LOCK_CONFLICT: 'CLINICAL_ORDER_OPTIMISTIC_LOCK_CONFLICT',
    CLINICAL_APPROVAL_ROLE_NOT_AUTHORIZED: 'CLINICAL_APPROVAL_ROLE_NOT_AUTHORIZED',
    VALIDATION_ERROR: 'CLINICAL_VALIDATION_ERROR',
};
exports.ClinicalErrorCatalog = {
    [exports.ClinicalErrorCodes.ENCOUNTER_INVALID_STATUS_TRANSITION]: {
        httpStatus: 409,
        description: 'This encounter cannot move to the requested status from its current status.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.ENCOUNTER_DOCUMENTATION_REQUIRED]: {
        httpStatus: 409,
        description: 'An encounter with zero recorded Clinical Documents cannot be marked complete.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.ENCOUNTER_ALREADY_FINALIZED]: {
        httpStatus: 409,
        description: 'A SIGNED or AMENDED encounter is a finalized clinical record — it cannot be cancelled.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.ENCOUNTER_OPTIMISTIC_LOCK_CONFLICT]: {
        httpStatus: 409,
        description: 'The encounter was modified by another user since it was last read.',
        retryable: true,
    },
    [exports.ClinicalErrorCodes.CLINICAL_DOCUMENT_TEMPLATE_INVALID]: {
        httpStatus: 400,
        description: 'The referenced Clinical Template is inactive, or its documentType does not match the requested document.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.CLINICAL_DOCUMENT_NOT_DRAFT]: {
        httpStatus: 409,
        description: 'Only a DRAFT document can be updated — this one has already been signed or finalized.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.CLINICAL_DOCUMENT_INVALID_STATUS_TRANSITION]: {
        httpStatus: 409,
        description: 'This document cannot move to the requested status from its current status.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.CLINICAL_DOCUMENT_NOT_FINAL]: {
        httpStatus: 409,
        description: 'Only a FINAL document can be amended.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.CLINICAL_DOCUMENT_OPTIMISTIC_LOCK_CONFLICT]: {
        httpStatus: 409,
        description: 'The document was modified by another user since it was last read.',
        retryable: true,
    },
    [exports.ClinicalErrorCodes.CLINICAL_ORDER_INVALID_REFERENCE]: {
        httpStatus: 400,
        description: 'The supplied target reference does not match exactly one field for the given orderType.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.CLINICAL_ORDER_DUPLICATE_ACTIVE]: {
        httpStatus: 409,
        description: 'An active order of the same type already exists for this encounter against the same reference.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.CLINICAL_ORDER_INVALID_STATUS_TRANSITION]: {
        httpStatus: 409,
        description: 'This order cannot move to the requested status from its current status.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.CLINICAL_ORDER_ALREADY_CANCELLED]: {
        httpStatus: 409,
        description: 'This order is already CANCELLED — it cannot be cancelled again.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.CLINICAL_ORDER_OPTIMISTIC_LOCK_CONFLICT]: {
        httpStatus: 409,
        description: 'The order was modified by another user since it was last read.',
        retryable: true,
    },
    [exports.ClinicalErrorCodes.CLINICAL_APPROVAL_ROLE_NOT_AUTHORIZED]: {
        httpStatus: 403,
        description: 'The supplied co-signer/signer role IDs do not intersect the roles required by the resolved ApprovalMatrix chain.',
        retryable: false,
    },
    [exports.ClinicalErrorCodes.VALIDATION_ERROR]: {
        httpStatus: 422,
        description: 'Request body failed schema validation.',
        retryable: false,
    },
};
function getClinicalErrorCatalogEntry(code) {
    return exports.ClinicalErrorCatalog[code];
}
//# sourceMappingURL=clinical-error-codes.js.map